import {
  convertToDisplayDateFormatddmmmyyyy,
  convertToInputDateFormat,
} from '../../../../../utils/dateformatter';
import { formatNumber } from '../../../../../utils/numberformatter';
import { fetchIncotermTimeline } from '../../../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import { api } from '../../../../../config';
import { EMPTY_COUNTER_FORM } from '../../../../../shared/offer-domain/constants';
import { FOB_INCOTERM_ID, STRAITS_ROUTE_ID } from './constants';
import {
  CARGO_RATIO_LABELS,
  getCargoRatioKey,
} from '../../../../TenderOpening/components/Detail/HpsParcel/lpg/utils';
import {
  unwrapApiItems,
  unwrapApiData,
} from '../../../../../shared/api/unwrap';

const isFilled = value =>
  value !== null && value !== undefined && String(value).trim() !== '';

export { unwrapApiItems, unwrapApiData };

export const fetchProductTypePricingUnit = async (productTypeId, token) => {
  if (!productTypeId || !token) return null;
  const response = await fetch(`${api.API_URL}/product-type/${productTypeId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  if (!response.ok) return null;
  const data = await response.json();
  return (
    data?.pricingUnit ??
    data?.PricingUnit ??
    data?.data?.pricingUnit ??
    data?.data?.PricingUnit ??
    null
  );
};

/** Same as HpsParcel getFobLocationNames */
export const getFobLocationNames = data =>
  (Array.isArray(data?.incotermFOBLocations) ? data.incotermFOBLocations : [])
    .map(location => location?.name ?? location?.Name ?? location)
    .filter(isFilled);

export const normalizeLocationIds = value => {
  if (Array.isArray(value)) {
    return value.map(item => item?.id ?? item?.value ?? item).filter(isFilled);
  }
  if (value?.id) return [value.id];
  if (isFilled(value)) return [value];
  return [];
};

export const resolveNamedLocation = value => {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    const names = value
      .map(item => resolveNamedLocation(item))
      .filter(isFilled);
    return names.length ? names.join(', ') : null;
  }
  if (typeof value === 'object') {
    return (
      value.name ??
      value.Name ??
      value.location?.name ??
      value.location?.Name ??
      value.Location?.name ??
      value.Location?.Name ??
      null
    );
  }
  return String(value).trim() || null;
};

export const formatFobLocationsJoined = (names = []) =>
  (Array.isArray(names) ? names : []).filter(isFilled).join(' or ');

/** Selected capable origin labels — no "or" (reserved for FOB Straits). */
export const formatSelectedOriginLocations = (names = []) =>
  (Array.isArray(names) ? names : []).filter(isFilled).join(', ');

export const isFobOriginDisplay = (route, names = []) => {
  const routeText = String(route || '')
    .trim()
    .toLowerCase();
  if (routeText === 'origin') return true;
  if (routeText === 'straits') return false;
  return (Array.isArray(names) ? names : []).filter(isFilled).length > 2;
};

export const resolveFobPortLabel = (item, route = null) => {
  const fromNames = getFobLocationNames(item);
  if (!fromNames.length) return null;
  if (fromNames.length === 1) return fromNames[0];
  const resolvedRoute =
    route ?? item?.route ?? item?.incotermPortType ?? item?.incotermRouteLabel;
  if (isFobOriginDisplay(resolvedRoute, fromNames)) return 'All Origin';
  return formatFobLocationsJoined(fromNames);
};

/** Names of FOB locations marked capable via listIsCapable (submitted-bidder / delivery-detail). */
export const getCapableOriginNamesFromFobLocations = (
  incotermFOBLocations = []
) =>
  (Array.isArray(incotermFOBLocations) ? incotermFOBLocations : [])
    .filter(loc => {
      const flag = loc?.listIsCapable ?? loc?.ListIsCapable;
      if (Array.isArray(flag)) return flag.length > 0;
      return Boolean(flag);
    })
    .map(loc => loc?.name ?? loc?.Name ?? '')
    .filter(isFilled);

/** Selected capable origin IDs from get-delivery-detail (listIsCapable). */
export const resolveCapableLocationIds = (item = {}) => {
  const direct = item?.listIsCapable;
  if (Array.isArray(direct) && direct.length) {
    return normalizeLocationIds(direct).map(String);
  }

  const locations = Array.isArray(item?.incotermFOBLocations)
    ? item.incotermFOBLocations
    : [];

  const fromFlags = locations
    .filter(loc => {
      const flag = loc?.listIsCapable;
      if (Array.isArray(flag)) return flag.length > 0;
      return Boolean(flag);
    })
    .map(loc => loc?.id ?? loc?.Id)
    .filter(isFilled)
    .map(String);

  if (fromFlags.length) return fromFlags;

  const nestedIds = [];
  locations.forEach(loc => {
    if (!Array.isArray(loc?.listIsCapable)) return;
    loc.listIsCapable.forEach(entry => {
      const id = entry?.id ?? entry?.Id ?? entry?.value ?? entry;
      if (isFilled(id)) nestedIds.push(String(id));
    });
  });
  return nestedIds;
};

export const resolveCapableLocationSelections = (item = {}) => {
  const ids = resolveCapableLocationIds(item);
  if (!ids.length) return [];

  const locations = Array.isArray(item?.incotermFOBLocations)
    ? item.incotermFOBLocations
    : [];
  const idSet = new Set(ids);
  const matched = locations
    .filter(loc => idSet.has(String(loc?.id ?? loc?.Id ?? '')))
    .map(loc => ({
      id: String(loc?.id ?? loc?.Id ?? ''),
      name: loc?.name ?? loc?.Name ?? '',
    }))
    .filter(loc => loc.id);

  if (matched.length) return matched;
  return ids.map(id => ({ id: String(id), name: '' }));
};

export const resolveCapableLocationSelection = (item = {}) =>
  resolveCapableLocationSelections(item)[0] || null;

export const isFobOriginItem = (item = {}) => {
  const incoterm = String(item?.incoterm || '')
    .trim()
    .toUpperCase();
  if (incoterm !== 'FOB') return false;
  return isFobOriginDisplay(item?.route, getFobLocationNames(item));
};

const normalizePricingValue = value =>
  value === null || value === undefined ? '' : value;

const pickFilledOrFallback = (primary, fallback) => {
  if (
    primary !== null &&
    primary !== undefined &&
    String(primary).trim() !== ''
  ) {
    return primary;
  }
  if (
    fallback !== null &&
    fallback !== undefined &&
    String(fallback).trim() !== ''
  ) {
    return fallback;
  }
  return '';
};

const pickFobLocationPricing = (loc, fallbackItem, useFallback) => {
  const dyed = normalizePricingValue(
    pickFilledOrFallback(loc?.dyed, fallbackItem?.dyed)
  );
  const undyed = normalizePricingValue(
    pickFilledOrFallback(loc?.undyed, fallbackItem?.undyed)
  );
  const alpha = normalizePricingValue(
    pickFilledOrFallback(loc?.alpha, fallbackItem?.alpha)
  );
  const improvementDyed = normalizePricingValue(
    pickFilledOrFallback(
      loc?.improvementDyed,
      useFallback ? fallbackItem?.improvementDyed : ''
    )
  );
  const improvementUndyed = normalizePricingValue(
    pickFilledOrFallback(
      loc?.improvementUndyed,
      useFallback ? fallbackItem?.improvementUndyed : ''
    )
  );
  const improvementAlpha = normalizePricingValue(
    pickFilledOrFallback(
      loc?.improvementAlpha,
      useFallback ? fallbackItem?.improvementAlpha : ''
    )
  );
  return {
    dyed,
    undyed,
    alpha,
    unitBbl: dyed,
    cbm: undyed,
    improvementDyed,
    improvementUndyed,
    improvementAlpha,
  };
};

/** Map get-delivery-parcel listData.id → Map(incotermLocationId → location). */
export const indexParcelFobLocationsByDetail = (parcelGroups = []) => {
  const byDetail = new Map();
  (Array.isArray(parcelGroups) ? parcelGroups : []).forEach(group => {
    const listData = group?.listData || group?.ListData || [];
    (Array.isArray(listData) ? listData : []).forEach(details => {
      const detailId = String(details?.id ?? '');
      if (!detailId) return;
      const locMap = new Map();
      const locations = Array.isArray(details?.incotermFOBLocations)
        ? details.incotermFOBLocations
        : [];
      locations.forEach((loc, index) => {
        const locId = String(loc?.id ?? loc?.Id ?? '');
        if (!locId) return;
        locMap.set(locId, { ...loc, locationSlot: index + 1 });
      });
      byDetail.set(detailId, locMap);
    });
  });
  return byDetail;
};

export const mergeParcelFobOriginPricing = (items = [], parcelByDetail) => {
  if (!parcelByDetail?.size) return items;
  return items.map(item => {
    if (!isFobOriginItem(item)) return item;
    const locMap = parcelByDetail.get(
      String(item.quantityInformationDetailId ?? '')
    );
    if (!locMap?.size) return item;
    const locations = (
      Array.isArray(item.incotermFOBLocations) ? item.incotermFOBLocations : []
    ).map(loc => {
      const locId = String(loc?.id ?? loc?.Id ?? '');
      const fromParcel = locMap.get(locId);
      if (!fromParcel) return loc;
      return {
        ...loc,
        dyed: fromParcel.dyed ?? loc.dyed,
        undyed: fromParcel.undyed ?? loc.undyed,
        dyed2: fromParcel.dyed2 ?? loc.dyed2,
        undyed2: fromParcel.undyed2 ?? loc.undyed2,
        dyed3: fromParcel.dyed3 ?? loc.dyed3,
        undyed3: fromParcel.undyed3 ?? loc.undyed3,
        alpha: fromParcel.alpha ?? loc.alpha,
        alpha2: fromParcel.alpha2 ?? loc.alpha2,
        alpha3: fromParcel.alpha3 ?? loc.alpha3,
        improvementDyed: fromParcel.improvementDyed ?? loc.improvementDyed,
        improvementUndyed:
          fromParcel.improvementUndyed ?? loc.improvementUndyed,
        improvementAlpha: fromParcel.improvementAlpha ?? loc.improvementAlpha,
        improvementDyed2: fromParcel.improvementDyed2 ?? loc.improvementDyed2,
        improvementUndyed2:
          fromParcel.improvementUndyed2 ?? loc.improvementUndyed2,
        improvementAlpha2:
          fromParcel.improvementAlpha2 ?? loc.improvementAlpha2,
        locationSlot: fromParcel.locationSlot ?? loc.locationSlot ?? null,
      };
    });
    return { ...item, incotermFOBLocations: locations };
  });
};

/** FOB Origin: 0→1 row; 1→1 row; N>1→N rows (per capable location). */
export const expandFobOriginCapableRows = (items = []) =>
  (Array.isArray(items) ? items : []).flatMap(item => {
    if (!isFobOriginItem(item)) {
      return [
        {
          ...item,
          incotermLocationId: null,
          fobOriginLocationName: null,
          locationSlot: null,
        },
      ];
    }

    const selections =
      Array.isArray(item.selectedCapableLocations) &&
      item.selectedCapableLocations.length
        ? item.selectedCapableLocations
        : resolveCapableLocationSelections(item);

    if (!selections.length) {
      return [
        {
          ...item,
          incotermLocationId: null,
          fobOriginLocationName: null,
          locationSlot: null,
        },
      ];
    }

    const useFallback = selections.length === 1;
    const locations = Array.isArray(item.incotermFOBLocations)
      ? item.incotermFOBLocations
      : [];

    return selections.map((sel, index) => {
      const locId = String(sel?.id ?? '');
      const loc =
        locations.find(
          entry => String(entry?.id ?? entry?.Id ?? '') === locId
        ) || null;
      const pricing = pickFobLocationPricing(loc, item, useFallback);
      return {
        ...item,
        ...pricing,
        incotermLocationId: locId || null,
        fobOriginLocationName: sel?.name || loc?.name || loc?.Name || '',
        loadingPort: sel?.name || loc?.name || loc?.Name || item.loadingPort,
        locationSlot: null,
        isFobOriginLocationRow: true,
        isFobOriginGroupLead: index === 0,
        selectedCapableLocations: selections,
        capableLocationIds: selections.map(entry => String(entry.id)),
        listIsCapable: selections,
      };
    });
  });

export const rebuildFobOriginCapableRows = (
  items = [],
  detailId,
  selectedLocations = [],
  checked = true
) => {
  const targetId = String(detailId ?? '');
  if (!targetId) return items;

  const result = [];
  let baseHandled = false;

  (Array.isArray(items) ? items : []).forEach(item => {
    if (String(item?.quantityInformationDetailId ?? '') !== targetId) {
      result.push(item);
      return;
    }
    if (baseHandled) return;
    baseHandled = true;

    const base = {
      ...item,
      incotermLocationId: null,
      fobOriginLocationName: null,
      locationSlot: null,
      isFobOriginLocationRow: false,
      isFobOriginGroupLead: true,
      selectedCapableLocations: checked ? selectedLocations : [],
      capableLocationIds: checked
        ? selectedLocations.map(loc => String(loc.id))
        : [],
      listIsCapable: checked ? selectedLocations : [],
      capable: checked,
      ...(checked
        ? null
        : {
            dyed: '',
            undyed: '',
            alpha: '',
            cbm: '',
            unitBbl: '',
          }),
    };

    if (!checked || !isFobOriginItem(base)) {
      result.push(base);
      return;
    }

    expandFobOriginCapableRows([base]).forEach(row => result.push(row));
  });

  return result;
};

/** TE/IC/Offer: merge get-delivery-parcel FOB Origin pricing then expand capable rows. */
export const enrichItemsWithFobOriginParcel = async ({
  procurementId,
  token,
  items = [],
}) => {
  if (!Array.isArray(items) || !items.length) return items;

  let nextItems = items;
  const hasFobOrigin = nextItems.some(row => isFobOriginItem(row));
  if (hasFobOrigin && procurementId && token) {
    try {
      const response = await fetch(
        `${api.API_URL}/procurement/get-delivery-parcel?PageNumber=1&PageSize=100&procurementId=${procurementId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const result = await response.json();
        const parcelGroups = result?.data?.data || result?.data?.Data || [];
        nextItems = mergeParcelFobOriginPricing(
          nextItems,
          indexParcelFobLocationsByDetail(parcelGroups)
        );
      }
    } catch (error) {
      // keep base items when parcel enrichment fails
    }
  }

  return expandFobOriginCapableRows(nextItems);
};

export const toDateInputValue = dateStr => {
  if (!dateStr) return '';
  const parsed = new Date(dateStr);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return convertToInputDateFormat(dateStr) || '';
};

export const getRouteIdByIncoterm = (route = null, incotermId = null) => {
  const routeMap = {
    'e0173dfd-e109-4054-8640-23d95befd327':
      'e0173dfd-e109-4054-8640-23d95befd328',
    'e0173dfd-e109-4054-8640-23d95befd328':
      'e0173dfd-e109-4054-8640-23d95befd329',
    'e0173dfd-e109-4054-8640-23d95befd329':
      'e0173dfd-e109-4054-8640-23d95befd330',
    'e0173dfd-e109-4054-8640-23d95befd326':
      'e0173dfd-e109-4054-8640-23d95befd326',
  };

  if (route === STRAITS_ROUTE_ID) return STRAITS_ROUTE_ID;
  if (route === FOB_INCOTERM_ID) return FOB_INCOTERM_ID;
  if (route) return route;

  if (incotermId && routeMap[incotermId]) return routeMap[incotermId];
  return incotermId;
};

export const getMultiSelectValue = (options, ids) => {
  const selectedIds = (Array.isArray(ids) ? ids : ids ? [ids] : []).map(String);
  return options.filter(option => selectedIds.includes(String(option.value)));
};

export const pickDetailValue = (details, ...keys) => {
  for (const key of keys) {
    const value = details?.[key];
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
  }
  return null;
};

export const getListData = item => item?.listData || item?.ListData || [];

const hasMeaningfulValue = value =>
  value !== null && value !== undefined && value !== '';

export const PROCUREMENT_INCOTERM_PASSTHROUGH_FIELDS = [
  'timelineType2',
  'timelineType3',
  'loc1Draft1',
  'loc1Draft2',
  'loc1Loa1',
  'loc1Loa2',
  'loc1Dwt1',
  'loc1Dwt2',
  'loc1NotesForSTS',
  'loc2Draft1',
  'loc2Draft2',
  'loc2Loa1',
  'loc2Loa2',
  'loc2Dwt1',
  'loc2Dwt2',
  'loc2NotesForSTS',
  'loc3Draft1',
  'loc3Draft2',
  'loc3Loa1',
  'loc3Loa2',
  'loc3Dwt1',
  'loc3Dwt2',
  'loc3NotesForSTS',
  'additionalInformation',
];

export const preserveProcurementIncotermFields = (
  counterForm,
  procurementForm
) => {
  const preserved = {};
  PROCUREMENT_INCOTERM_PASSTHROUGH_FIELDS.forEach(field => {
    preserved[field] = counterForm?.[field] ?? procurementForm?.[field] ?? null;
  });
  return { ...counterForm, ...preserved };
};

export const resolveCounterMultiportFlags = (data = {}, rowData = {}) => {
  const multiportFromData = data?.multiportOption ?? '';
  const portTypeFromData = data?.incotermPortType ?? '';

  if (multiportFromData === '2Port' || multiportFromData === '3Port') {
    return {
      incotermPortType: portTypeFromData || 'MultiPort',
      incotermMultiPortType: multiportFromData,
    };
  }

  if (portTypeFromData === 'SinglePort') {
    return {
      incotermPortType: portTypeFromData,
      incotermMultiPortType: '',
    };
  }

  const hasPort3 = !!(
    hasMeaningfulValue(data?.incotermLocation3?.id) ||
    hasMeaningfulValue(data?.incotermLocation3Id) ||
    hasMeaningfulValue(rowData?.port3) ||
    hasMeaningfulValue(rowData?.parcelSize3) ||
    hasMeaningfulValue(rowData?.counterPort3) ||
    hasMeaningfulValue(rowData?.port3StartDate)
  );
  const hasPort2 = !!(
    hasPort3 ||
    hasMeaningfulValue(data?.incotermLocation2?.id) ||
    hasMeaningfulValue(data?.incotermLocation2Id) ||
    hasMeaningfulValue(rowData?.port2) ||
    hasMeaningfulValue(rowData?.parcelSize2) ||
    hasMeaningfulValue(rowData?.counterPort2) ||
    hasMeaningfulValue(rowData?.port2StartDate)
  );

  if (hasPort3) {
    return { incotermPortType: 'MultiPort', incotermMultiPortType: '3Port' };
  }
  if (hasPort2) {
    return { incotermPortType: 'MultiPort', incotermMultiPortType: '2Port' };
  }

  return {
    incotermPortType: portTypeFromData || '',
    incotermMultiPortType: multiportFromData || '',
  };
};

export const mapIncotermDetailToCounterForm = (
  data,
  rowData,
  _headerForm,
  { fromCounter = false, incotermRouteOptions = [] } = {}
) => {
  const incotermId = data?.incoterm?.id ?? data?.incotermId ?? '';
  const isFob = String(incotermId) === String(FOB_INCOTERM_ID);
  const routeId =
    data?.incotermRoute?.id ??
    data?.incotermRoute?.incotermId ??
    data?.incotermRouteId ??
    '';
  const routeLabel =
    data?.incotermRoute?.name ??
    data?.incotermRoute?.Name ??
    incotermRouteOptions.find(
      option => String(option.value) === String(routeId)
    )?.label ??
    rowData?.counterRoute ??
    '';

  const qtyDetail = data?.quantityInformationDetail?.[0];
  const multiportFlags = isFob
    ? { incotermPortType: '', incotermMultiPortType: '' }
    : resolveCounterMultiportFlags(data, rowData);

  const pickDate = (sourceDate, counterDate, originalDate) =>
    toDateInputValue(
      sourceDate ??
        (fromCounter ? counterDate : (counterDate ?? originalDate)) ??
        ''
    );

  const pickQuantity = (sourceQty, counterQty, originalQty) => {
    if (sourceQty !== null && sourceQty !== undefined && sourceQty !== '') {
      return sourceQty;
    }
    if (fromCounter) {
      return counterQty ?? '';
    }
    return counterQty ?? originalQty ?? '';
  };

  return {
    ...EMPTY_COUNTER_FORM,
    deliveryBasis:
      rowData?.deliveryBasis ??
      rowData?.counterDeliveryBasis ??
      data?.deliveryBasis ??
      '',
    incotermId,
    incotermPortType: multiportFlags.incotermPortType,
    incotermMultiPortType: multiportFlags.incotermMultiPortType,
    incotermRouteId: routeId,
    incotermRouteLabel: routeLabel,
    incotermFOB: normalizeLocationIds(
      data?.incotermFOBLocations ?? data?.incotermFOB
    ),
    incotermLocationId:
      data?.incotermLocation?.id ?? data?.incotermLocationId ?? '',
    incotermLocation2Id: isFob
      ? normalizeLocationIds(
          data?.incotermFOB2Locations ??
            data?.incotermFOB2 ??
            data?.incotermLocation2Locations ??
            data?.incotermLocation2Id ??
            data?.incotermLocation2
        )
      : (data?.incotermLocation2?.id ?? data?.incotermLocation2Id ?? ''),
    incotermLocation3Id:
      data?.incotermLocation3?.id ?? data?.incotermLocation3Id ?? '',
    quantityLocation1: pickQuantity(
      qtyDetail?.quantityLocation1,
      rowData?.counterParcelSize,
      rowData?.parcelSize
    ),
    quantityLocation2: pickQuantity(
      qtyDetail?.quantityLocation2,
      rowData?.counterParcelSize2,
      rowData?.parcelSize2
    ),
    quantityLocation3: pickQuantity(
      qtyDetail?.quantityLocation3,
      rowData?.counterParcelSize3,
      rowData?.parcelSize3
    ),
    startDate: pickDate(
      data?.startDate,
      rowData?.counterStartDate,
      rowData?.port1StartDate
    ),
    endDate: pickDate(
      data?.endDate,
      rowData?.counterEndDate,
      rowData?.port1EndDate
    ),
    startDate2: pickDate(
      data?.startDate2,
      rowData?.counterStartDate2,
      rowData?.port2StartDate
    ),
    endDate2: pickDate(
      data?.endDate2,
      rowData?.counterEndDate2,
      rowData?.port2EndDate
    ),
    startDate3: pickDate(
      data?.startDate3,
      rowData?.counterStartDate3,
      rowData?.port3StartDate
    ),
    endDate3: pickDate(
      data?.endDate3,
      rowData?.counterEndDate3,
      rowData?.port3EndDate
    ),
    timelineType: data?.timelineType ?? (isFob ? 'ALD' : 'ADD'),
    timelineType2: data?.timelineType2 ?? null,
    timelineType3: data?.timelineType3 ?? null,
    loc1Draft1: data?.loc1Draft1 ?? null,
    loc1Draft2: data?.loc1Draft2 ?? null,
    loc1Loa1: data?.loc1Loa1 ?? null,
    loc1Loa2: data?.loc1Loa2 ?? null,
    loc1Dwt1: data?.loc1Dwt1 ?? null,
    loc1Dwt2: data?.loc1Dwt2 ?? null,
    loc1NotesForSTS: data?.loc1NotesForSTS ?? null,
    loc2Draft1: data?.loc2Draft1 ?? null,
    loc2Draft2: data?.loc2Draft2 ?? null,
    loc2Loa1: data?.loc2Loa1 ?? null,
    loc2Loa2: data?.loc2Loa2 ?? null,
    loc2Dwt1: data?.loc2Dwt1 ?? null,
    loc2Dwt2: data?.loc2Dwt2 ?? null,
    loc2NotesForSTS: data?.loc2NotesForSTS ?? null,
    loc3Draft1: data?.loc3Draft1 ?? null,
    loc3Draft2: data?.loc3Draft2 ?? null,
    loc3Loa1: data?.loc3Loa1 ?? null,
    loc3Loa2: data?.loc3Loa2 ?? null,
    loc3Dwt1: data?.loc3Dwt1 ?? null,
    loc3Dwt2: data?.loc3Dwt2 ?? null,
    loc3NotesForSTS: data?.loc3NotesForSTS ?? null,
    additionalInformation: data?.additionalInformation ?? null,
    quantityInformationDetailId: rowData?.quantityInformationDetailId ?? null,
    quantityIncotermId: rowData?.quantityIncotermId ?? data?.id ?? null,
  };
};

export const buildCounterFormFromRow = (
  rowData,
  _headerForm,
  incotermOptions = []
) => {
  const matchedIncotermId =
    incotermOptions.find(option => option.label === rowData?.incoterm)?.value ??
    '';
  const isFob = String(matchedIncotermId) === String(FOB_INCOTERM_ID);
  const multiportFlags = isFob
    ? { incotermPortType: '', incotermMultiPortType: '' }
    : resolveCounterMultiportFlags({}, rowData);

  return {
    ...EMPTY_COUNTER_FORM,
    deliveryBasis: rowData?.deliveryBasis ?? rowData?.parcelSize ?? '',
    incotermId: matchedIncotermId,
    incotermPortType: multiportFlags.incotermPortType,
    incotermMultiPortType: multiportFlags.incotermMultiPortType,
    quantityLocation1: rowData?.parcelSize ?? rowData?.deliveryBasis ?? '',
    quantityLocation2: rowData?.parcelSize2 ?? '',
    quantityLocation3: rowData?.parcelSize3 ?? '',
    startDate: toDateInputValue(rowData?.port1StartDate),
    endDate: toDateInputValue(rowData?.port1EndDate),
    startDate2: toDateInputValue(rowData?.port2StartDate),
    endDate2: toDateInputValue(rowData?.port2EndDate),
    startDate3: toDateInputValue(rowData?.port3StartDate),
    endDate3: toDateInputValue(rowData?.port3EndDate),
    timelineType: 'ADD',
    timelineType2: rowData?.timelineType2 ?? null,
    timelineType3: rowData?.timelineType3 ?? null,
    ...Object.fromEntries(
      PROCUREMENT_INCOTERM_PASSTHROUGH_FIELDS.filter(
        field => field !== 'timelineType2' && field !== 'timelineType3'
      ).map(field => [field, rowData?.[field] ?? null])
    ),
    quantityInformationDetailId: rowData?.quantityInformationDetailId ?? null,
    quantityIncotermId: rowData?.quantityIncotermId ?? null,
  };
};

export const mapDeliveryDetailRow = (parentItem, details) => {
  const incotermFOBLocations = Array.isArray(details?.incotermFOBLocations)
    ? details.incotermFOBLocations
    : details?.incotermFOBLocations
      ? [details.incotermFOBLocations]
      : [];
  const listIsCapable = Array.isArray(details?.listIsCapable)
    ? details.listIsCapable
    : [];
  const route = details?.route ?? null;
  const fobLocationNames = getFobLocationNames({ incotermFOBLocations });
  const capableLocationSelections = resolveCapableLocationSelections({
    incotermFOBLocations,
    listIsCapable,
  });
  const capableLocationSelection = capableLocationSelections[0] || null;
  const selectedCapableNames =
    getCapableOriginNamesFromFobLocations(incotermFOBLocations).length > 0
      ? getCapableOriginNamesFromFobLocations(incotermFOBLocations)
      : capableLocationSelections.map(loc => loc.name).filter(Boolean);
  const fobPort1 =
    formatSelectedOriginLocations(selectedCapableNames) ||
    resolveFobPortLabel({ incotermFOBLocations }, route);
  const fobPort2 = resolveNamedLocation(details?.incotermLocation2);

  return {
    id: parentItem.id,
    quantityInformationDetailId: details?.id ?? null,
    quantityInformationId: details?.quantityInformationId ?? null,
    quantityIncotermId: details?.quantityIncotermId ?? null,
    deliveryBasis: details?.deliveryBasis ?? null,
    incoterm: details?.incoterm ?? null,
    route,
    loadingPort: details?.loadingPort || fobPort1 || null,
    incotermFOBLocations,
    listIsCapable,
    capableLocationIds: capableLocationSelections.map(loc => loc.id),
    selectedCapableLocations: capableLocationSelections,
    selectedCapableLocation: capableLocationSelection,
    fobLocationNames,
    parcelSize:
      pickDetailValue(
        details,
        'parcelSize',
        'ParcelSize',
        'deliveryBasis',
        'DeliveryBasis'
      ) ?? null,
    port1: details?.port1 || fobPort1 || null,
    addAld: details?.addAld ?? null,
    parcelSize2: pickDetailValue(details, 'parcelSize2', 'ParcelSize2'),
    port2: details?.port2 || fobPort2 || null,
    addAld2: details?.addAld2 ?? null,
    parcelSize3: pickDetailValue(details, 'parcelSize3', 'ParcelSize3'),
    port3: details?.port3 ?? null,
    addAld3: details?.addAld3 ?? null,
    port1StartDate: details?.startDate ?? details?.startDate1 ?? null,
    port1EndDate: details?.endDate ?? details?.endDate1 ?? null,
    port2StartDate: details?.startDate2 ?? null,
    port2EndDate: details?.endDate2 ?? null,
    port3StartDate: details?.startDate3 ?? null,
    port3EndDate: details?.endDate3 ?? null,
    timelineType2: details?.timelineType2 ?? null,
    timelineType3: details?.timelineType3 ?? null,
    ...Object.fromEntries(
      PROCUREMENT_INCOTERM_PASSTHROUGH_FIELDS.filter(
        field => field !== 'timelineType2' && field !== 'timelineType3'
      ).map(field => [field, details?.[field] ?? null])
    ),
    cbm: details?.undyed ?? '',
    unitBbl: details?.dyed ?? '',
    dyed: details?.dyed ?? '',
    undyed: details?.undyed ?? '',
    alpha: details?.alphaMitra1 ?? '',
    cargoRatio: pickDetailValue(
      details,
      'cargoRatio',
      'CargoRatio',
      'ratioLpgId',
      'RatioLpgId'
    ),
    cargoRatioLabel: pickDetailValue(
      details,
      'cargoRatioLabel',
      'CargoRatioLabel',
      'ratioName',
      'RatioName'
    ),
    ratioLpg:
      details?.ratioLpg?.ratioName ??
      details?.ratioLpg?.name ??
      details?.ratioLpg ??
      null,
    dyed2: details?.dyed2 ?? '',
    undyed2: details?.undyed2 ?? '',
    counterIncoterm: details?.counterIncoterm ?? details?.incoterm2 ?? null,
    counterRoute: details?.counterRoute ?? details?.route2 ?? null,
    counterLoadingPort:
      details?.counterLoadingPort ??
      details?.counterPort1 ??
      details?.loadingPort2 ??
      null,
    counterParcelSize:
      pickDetailValue(
        details,
        'counterParcelSize',
        'parcelSizeCounter',
        'CounterParcelSize',
        'ParcelSizeCounter'
      ) ?? null,
    counterStartDate:
      details?.counterStartDate ?? details?.startDateCounter ?? null,
    counterEndDate: details?.counterEndDate ?? details?.endDateCounter ?? null,
    counterAddAld: details?.counterAddAld ?? details?.addAldCounter ?? null,
    counterDyed: details?.dyed2 ?? details?.counterDyed ?? '',
    counterUndyed: details?.undyed2 ?? details?.counterUndyed ?? '',
    counterAlpha:
      details?.alphaMitra2 ?? details?.counterAlpha ?? details?.alpha2 ?? '',
    alpha2: details?.alphaMitra2 ?? details?.alpha2 ?? '',
    counterOperationalTolerance:
      details?.counterOperationalTolerance ??
      details?.operationalTolerance2 ??
      null,
    isCapable:
      details?.isCapable ?? details?.capable ?? details?.isIsCapable ?? null,
    capable:
      details?.isCapable ?? details?.capable ?? details?.isIsCapable ?? null,
  };
};

export const enrichDeliveryItemsWithFobLocations = async ({
  quantityDeliveryTermId,
  token,
  items = [],
}) => {
  if (!items.length || !quantityDeliveryTermId || !token) return items;

  try {
    const { rawItems } = await fetchIncotermTimeline(
      quantityDeliveryTermId,
      token
    );
    if (!rawItems.length) return items;

    const byId = new Map();
    rawItems.forEach(raw => {
      const id = String(raw?.id ?? raw?.Id ?? '');
      if (!id) return;

      const incotermFOBLocations = Array.isArray(raw?.incotermFOBLocations)
        ? raw.incotermFOBLocations
        : raw?.incotermFOBLocations
          ? [raw.incotermFOBLocations]
          : [];
      const route =
        raw?.incotermRoute?.name ??
        raw?.incotermRoute?.Name ??
        raw?.incotermPortType ??
        null;
      const fobPort1 = resolveFobPortLabel({ incotermFOBLocations }, route);
      const fobPort2 = resolveNamedLocation(raw?.incotermLocation2);

      byId.set(id, {
        incotermFOBLocations,
        fobLocationNames: getFobLocationNames({ incotermFOBLocations }),
        fobPort1,
        fobPort2,
        route,
        startDate: raw?.startDate ?? raw?.StartDate ?? null,
        endDate: raw?.endDate ?? raw?.EndDate ?? null,
        startDate2: raw?.startDate2 ?? raw?.StartDate2 ?? null,
        endDate2: raw?.endDate2 ?? raw?.EndDate2 ?? null,
        isImprovement: raw?.isImprovement ?? raw?.IsImprovement ?? false,
        closingDateImprovement:
          raw?.closingDateImprovement ??
          raw?.ClosingDateImprovement ??
          raw?.dateImprovement ??
          raw?.DateImprovement ??
          null,
      });
    });

    return items.map(item => {
      const meta = byId.get(String(item.quantityIncotermId ?? ''));
      if (!meta) return item;

      const port1 = item.port1 || item.loadingPort || meta.fobPort1 || null;
      const port2 = item.port2 || meta.fobPort2 || null;
      const fobLocationNames =
        getFobLocationNames(item).length > 0
          ? getFobLocationNames(item)
          : meta.fobLocationNames;

      return {
        ...item,
        incotermFOBLocations:
          item.incotermFOBLocations?.length > 0
            ? item.incotermFOBLocations
            : meta.incotermFOBLocations,
        fobLocationNames,
        port1,
        loadingPort: item.loadingPort || meta.fobPort1 || null,
        port2,
        route: item.route || meta.route,
        port1StartDate: item.port1StartDate || meta.startDate,
        port1EndDate: item.port1EndDate || meta.endDate,
        port2StartDate: item.port2StartDate || meta.startDate2,
        port2EndDate: item.port2EndDate || meta.endDate2,
        isImprovement: [
          item.isImprovement,
          item.IsImprovement,
          meta.isImprovement,
        ].some(
          value =>
            value === true || value === 1 || value === '1' || value === 'true'
        ),
        closingDateImprovement:
          item.closingDateImprovement ??
          item.ClosingDateImprovement ??
          item.dateImprovement ??
          item.DateImprovement ??
          meta.closingDateImprovement ??
          null,
      };
    });
  } catch (error) {
    return items;
  }
};

export const formatIncotermRoute = (incoterm, route) => {
  if (incoterm && route) return `${incoterm} - ${route}`;
  return incoterm || route || '-';
};

export const hasCounterOffer = data =>
  !!(
    data.quantityIncotermCounterId ||
    data.quantityInformationCounterId ||
    data.quantityInformationDetailCounterId ||
    data.counterIncoterm ||
    data.counterRoute ||
    data.counterLoadingPort ||
    data.counterParcelSize ||
    data.counterDeliveryBasis ||
    data.counterOperationalTolerance
  );

const fetchCounterJson = async (path, token) => {
  const response = await fetch(`${api.API_URL}/${path}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const unwrapCounterList = result => {
  const data = unwrapApiData(result);
  if (Array.isArray(data)) return data;
  return data?.items ?? data?.Items ?? unwrapApiItems(result) ?? [];
};

const findOptionLabel = (options, value) =>
  options.find(option => String(option.value) === String(value))?.label ?? null;

const resolveCounterPortLabel = (incotermCounter, isFob) => {
  if (isFob) {
    const fobNames = getFobLocationNames({
      incotermFOBLocations:
        incotermCounter?.incotermFOBLocations ??
        incotermCounter?.incotermFOB ??
        [],
    });
    if (fobNames.length) {
      const route =
        incotermCounter?.incotermRoute?.name ??
        incotermCounter?.incotermRoute?.Name ??
        incotermCounter?.incotermRouteLabel ??
        incotermCounter?.incotermPortType;
      if (isFobOriginDisplay(route, fobNames)) {
        return formatSelectedOriginLocations(fobNames);
      }
      return formatFobLocationsJoined(fobNames);
    }
  }

  return (
    resolveNamedLocation(incotermCounter?.incotermLocation) ||
    resolveNamedLocation(incotermCounter?.incotermLocation1) ||
    null
  );
};

const resolveCounterFobLocationNames = (incotermCounter, isFob) => {
  if (!isFob) return [];
  return getFobLocationNames({
    incotermFOBLocations:
      incotermCounter?.incotermFOBLocations ??
      incotermCounter?.incotermFOB ??
      [],
  });
};

const resolveCounterPort2Label = (incotermCounter, isFob) => {
  if (isFob) {
    const fob2Names = getFobLocationNames({
      incotermFOBLocations:
        incotermCounter?.incotermFOB2Locations ??
        incotermCounter?.incotermFOB2 ??
        [],
    });
    if (fob2Names.length) {
      return formatFobLocationsJoined(fob2Names);
    }
  }

  return (
    resolveNamedLocation(incotermCounter?.incotermLocation2) ||
    resolveNamedLocation(incotermCounter?.incotermLocation2Locations) ||
    null
  );
};

const getCounterActivePorts = ({
  isFob,
  incotermPortType,
  multiportOption,
}) => {
  // FOB (incl. Origin/Straits) always single location — same as Procurement.
  if (isFob) {
    return { showPort1: true, showPort2: false, showPort3: false };
  }

  const isMulti = incotermPortType === 'MultiPort';
  const showPort2 =
    isMulti && (multiportOption === '2Port' || multiportOption === '3Port');
  const showPort3 = isMulti && multiportOption === '3Port';

  return { showPort1: true, showPort2, showPort3 };
};

const parseIncotermDate = value => {
  if (!value) return null;
  const parts = String(value).split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts.map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const splitDeliveryBasisByMultiport = (deliveryBasis, multiportType) => {
  const total = getDeliveryBasisTotal(deliveryBasis);
  if (!total) {
    return {
      quantityLocation1: '',
      quantityLocation2: '',
      quantityLocation3: '',
    };
  }

  if (multiportType === '2Port') {
    const location1 = Math.floor(total / 2);
    return {
      quantityLocation1: String(location1),
      quantityLocation2: String(total - location1),
      quantityLocation3: '',
    };
  }

  if (multiportType === '3Port') {
    const location1 = Math.floor(total / 3);
    const location2 = Math.floor(total / 3);
    return {
      quantityLocation1: String(location1),
      quantityLocation2: String(location2),
      quantityLocation3: String(total - location1 - location2),
    };
  }

  return {
    quantityLocation1: '',
    quantityLocation2: '',
    quantityLocation3: '',
  };
};

export const validateCounterIncoterm = (
  formData,
  { requiresCargoRatio = false, skipLocationQuantity = false } = {}
) => {
  const errors = {};
  const isFob = formData.incotermId === FOB_INCOTERM_ID;
  const isMultiPort = formData.incotermPortType === 'MultiPort';
  const is2Port = formData.incotermMultiPortType === '2Port';
  const is3Port = formData.incotermMultiPortType === '3Port';
  const needsPort2 = !isFob && isMultiPort && (is2Port || is3Port);
  const needsPort3 = !isFob && isMultiPort && is3Port;

  if (!formData.incotermId) {
    errors.incotermId = 'Please select Incoterm Type';
  }
  if (!isFob && !formData.incotermPortType) {
    errors.incotermPortType = 'Please select Port(s)';
  }
  if (!isFob && isMultiPort && !formData.incotermMultiPortType) {
    errors.incotermMultiPortType = 'Please select Multiport Option';
  }
  if (isFob && !formData.incotermRouteId) {
    errors.incotermRouteId = 'Please select Route';
  }
  if (!skipLocationQuantity) {
    if (needsPort2 && !hasMeaningfulValue(formData.quantityLocation1)) {
      errors.quantityLocation1 = 'Location 1 quantity is required';
    }
    if (needsPort2 && !hasMeaningfulValue(formData.quantityLocation2)) {
      errors.quantityLocation2 = 'Location 2 quantity is required';
    }
    if (needsPort3 && !hasMeaningfulValue(formData.quantityLocation3)) {
      errors.quantityLocation3 = 'Location 3 quantity is required';
    }
  }
  if (!isFob && formData.incotermId && !formData.incotermLocationId) {
    errors.incotermLocationId = 'Location is required';
  }
  if (needsPort2 && !formData.incotermLocation2Id) {
    errors.incotermLocation2Id = 'Location 2 is required';
  }
  if (needsPort3 && !formData.incotermLocation3Id) {
    errors.incotermLocation3Id = 'Location 3 is required';
  }
  if (!formData.startDate) errors.startDate = 'Date From is required';
  if (!formData.endDate) errors.endDate = 'Date To is required';
  if (needsPort2) {
    if (!formData.startDate2) errors.startDate2 = 'Date From is required';
    if (!formData.endDate2) errors.endDate2 = 'Date To is required';
  }
  if (needsPort3) {
    if (!formData.startDate3) errors.startDate3 = 'Date From is required';
    if (!formData.endDate3) errors.endDate3 = 'Date To is required';
  }

  [
    ['startDate', 'endDate'],
    ['startDate2', 'endDate2'],
    ['startDate3', 'endDate3'],
  ].forEach(([startField, endField]) => {
    const start = parseIncotermDate(formData[startField]);
    const end = parseIncotermDate(formData[endField]);
    if (start && end && start >= end) {
      errors[endField] = 'Date To must be after Date From';
    }
  });

  if (requiresCargoRatio && !(formData.selectedRatios?.length > 0)) {
    errors.selectedRatios = 'Please select the cargo ratio to proceed.';
  }

  return errors;
};

const normalizeComparableValue = value => {
  if (Array.isArray(value)) {
    return value.map(String).sort();
  }
  return value === null || value === undefined ? '' : String(value);
};

const normalizeComparableNumber = value => {
  const parsed = parseNumericQuantity(value);
  return parsed === null ? '' : parsed;
};

export const hasCounterFormChanges = (
  counterForm,
  originalForm,
  { isLpg = false, requiresCargoRatio = false } = {}
) => {
  if (!originalForm) return false;

  const isFob = counterForm.incotermId === FOB_INCOTERM_ID;
  const isMultiPort = !isFob && counterForm.incotermPortType === 'MultiPort';
  const isThreePort =
    isMultiPort && counterForm.incotermMultiPortType === '3Port';

  const snapshot = formData => {
    const result = {
      incotermId: normalizeComparableValue(formData.incotermId),
      startDate: toDateInputValue(formData.startDate),
      endDate: toDateInputValue(formData.endDate),
    };

    if (!isLpg) {
      result.deliveryBasis = normalizeComparableNumber(formData.deliveryBasis);
    }

    if (isFob) {
      result.incotermRouteId = normalizeComparableValue(
        formData.incotermRouteId
      );
      result.incotermFOB = normalizeComparableValue(formData.incotermFOB);
    } else {
      result.incotermPortType = normalizeComparableValue(
        formData.incotermPortType
      );
      result.incotermLocationId = normalizeComparableValue(
        formData.incotermLocationId
      );
    }

    if (isMultiPort) {
      result.incotermMultiPortType = normalizeComparableValue(
        formData.incotermMultiPortType
      );
      result.incotermLocation2Id = normalizeComparableValue(
        formData.incotermLocation2Id
      );
      result.quantityLocation1 = normalizeComparableNumber(
        formData.quantityLocation1
      );
      result.quantityLocation2 = normalizeComparableNumber(
        formData.quantityLocation2
      );
      result.startDate2 = toDateInputValue(formData.startDate2);
      result.endDate2 = toDateInputValue(formData.endDate2);
    }

    if (isThreePort) {
      result.incotermLocation3Id = normalizeComparableValue(
        formData.incotermLocation3Id
      );
      result.quantityLocation3 = normalizeComparableNumber(
        formData.quantityLocation3
      );
      result.startDate3 = toDateInputValue(formData.startDate3);
      result.endDate3 = toDateInputValue(formData.endDate3);
    }

    if (requiresCargoRatio) {
      result.selectedRatios = (formData.selectedRatios || [])
        .map(ratio => getCargoRatioKey(ratio?.id ?? ratio))
        .sort();
    }

    return result;
  };

  return (
    JSON.stringify(snapshot(counterForm)) !==
    JSON.stringify(snapshot(originalForm))
  );
};

const pickPreservedCounterPricing = item => {
  const pickAlpha = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value).trim();
      if (!text || text === '-') continue;
      if (Number(text) === 0) continue;
      return value;
    }
    return '';
  };
  const counterAlpha = pickAlpha(
    item?.counterAlpha,
    item?.alpha2,
    item?.Alpha2,
    item?.alphaMitra2,
    item?.AlphaMitra2
  );
  const counterDyed = pickAlpha(item?.counterDyed, item?.dyed2, item?.Dyed2);
  const counterUndyed = pickAlpha(
    item?.counterUndyed,
    item?.undyed2,
    item?.Undyed2
  );
  return {
    counterAlpha,
    alpha2: pickAlpha(item?.alpha2, item?.Alpha2, counterAlpha),
    counterDyed,
    counterUndyed,
  };
};

const EMPTY_COUNTER_ROW_FIELDS = {
  quantityIncotermCounterId: null,
  quantityInformationCounterId: null,
  quantityInformationDetailCounterId: null,
  counterDeliveryBasis: null,
  counterIncoterm: null,
  counterRoute: null,
  counterLoadingPort: null,
  counterFobLocationNames: [],
  counterParcelSize: null,
  counterStartDate: null,
  counterEndDate: null,
  counterAddAld: null,
  counterPort2: null,
  counterParcelSize2: null,
  counterStartDate2: null,
  counterEndDate2: null,
  counterAddAld2: null,
  counterPort3: null,
  counterParcelSize3: null,
  counterStartDate3: null,
  counterEndDate3: null,
  counterAddAld3: null,
  counterDyed: '',
  counterUndyed: '',
  counterAlpha: '',
  alpha2: '',
  counterAlphaImprovement: null,
  counterCargoRatios: [],
  counterOperationalTolerance: null,
  counterIsImprovement: false,
  counterClosingDateImprovement: null,
};

const mapCounterEntitiesToRowFields = ({
  incotermCounter,
  informationCounter,
  detailCounter,
  incotermTypeOptions = [],
  incotermRouteOptions = [],
}) => {
  const incotermId =
    incotermCounter?.incoterm?.id ?? incotermCounter?.incotermId ?? null;
  const isFob = String(incotermId) === String(FOB_INCOTERM_ID);
  const incotermRouteId =
    incotermCounter?.incotermRoute?.id ??
    incotermCounter?.incotermRouteId ??
    null;
  const incotermRouteLabel =
    incotermCounter?.incotermRoute?.name ??
    findOptionLabel(incotermRouteOptions, incotermRouteId) ??
    null;
  const { showPort2, showPort3 } = getCounterActivePorts({
    isFob,
    incotermPortType: incotermCounter?.incotermPortType,
    multiportOption: incotermCounter?.multiportOption,
    incotermRouteId,
    incotermRouteLabel,
  });

  const counterIncoterm =
    incotermCounter?.incoterm?.name ??
    findOptionLabel(incotermTypeOptions, incotermId);
  const counterRoute =
    incotermRouteLabel ??
    (isFob ? null : incotermCounter?.incotermPortType) ??
    null;

  const deliveryBasisQuantity =
    informationCounter?.quantity ?? detailCounter?.quantityLocation1 ?? null;
  const quantityLocation1 =
    detailCounter?.quantityLocation1 ??
    (showPort2 || showPort3 ? null : deliveryBasisQuantity);
  const quantityLocation2 = showPort2
    ? (detailCounter?.quantityLocation2 ?? null)
    : null;
  const quantityLocation3 = showPort3
    ? (detailCounter?.quantityLocation3 ?? null)
    : null;

  return {
    ...EMPTY_COUNTER_ROW_FIELDS,
    quantityIncotermCounterId:
      incotermCounter?.id ?? incotermCounter?.Id ?? null,
    counterIsImprovement:
      incotermCounter?.isImprovement ?? incotermCounter?.IsImprovement ?? false,
    counterClosingDateImprovement:
      incotermCounter?.closingDateImprovement ??
      incotermCounter?.ClosingDateImprovement ??
      incotermCounter?.dateImprovement ??
      incotermCounter?.DateImprovement ??
      null,
    quantityInformationCounterId:
      informationCounter?.id ?? informationCounter?.Id ?? null,
    quantityInformationDetailCounterId:
      detailCounter?.id ?? detailCounter?.Id ?? null,
    counterDeliveryBasis: deliveryBasisQuantity,
    counterIncoterm,
    counterRoute,
    counterLoadingPort: resolveCounterPortLabel(incotermCounter, isFob),
    counterFobLocationNames: resolveCounterFobLocationNames(
      incotermCounter,
      isFob
    ),
    counterParcelSize: quantityLocation1,
    counterStartDate: incotermCounter?.startDate ?? null,
    counterEndDate: incotermCounter?.endDate ?? null,
    counterAddAld: null,
    counterPort2: showPort2
      ? resolveCounterPort2Label(incotermCounter, isFob)
      : null,
    counterParcelSize2: quantityLocation2,
    counterStartDate2: showPort2 ? (incotermCounter?.startDate2 ?? null) : null,
    counterEndDate2: showPort2 ? (incotermCounter?.endDate2 ?? null) : null,
    counterAddAld2: null,
    counterPort3: showPort3
      ? resolveNamedLocation(incotermCounter?.incotermLocation3) || null
      : null,
    counterParcelSize3: quantityLocation3,
    counterStartDate3: showPort3 ? (incotermCounter?.startDate3 ?? null) : null,
    counterEndDate3: showPort3 ? (incotermCounter?.endDate3 ?? null) : null,
    counterAddAld3: null,
    counterDyed: detailCounter?.dyed2 ?? detailCounter?.dyed ?? '',
    counterUndyed: detailCounter?.undyed2 ?? detailCounter?.undyed ?? '',
    ...(() => {
      const nestedDetail =
        detailCounter?.quantityInformationDetail ??
        detailCounter?.QuantityInformationDetail ??
        null;
      const candidates = [
        nestedDetail?.alpha2,
        nestedDetail?.Alpha2,
        nestedDetail?.alphaMitra2,
        nestedDetail?.AlphaMitra2,
        nestedDetail?.alpha,
        nestedDetail?.Alpha,
        detailCounter?.alpha2,
        detailCounter?.Alpha2,
        detailCounter?.alphaMitra2,
        detailCounter?.AlphaMitra2,
        detailCounter?.alpha,
        detailCounter?.Alpha,
      ];
      const found =
        candidates.find(
          value =>
            value !== null && value !== undefined && String(value).trim() !== ''
        ) ?? '';
      return { counterAlpha: found, alpha2: found };
    })(),
    counterAlphaImprovement: (() => {
      const nestedDetail =
        detailCounter?.quantityInformationDetail ??
        detailCounter?.QuantityInformationDetail ??
        null;
      return (
        detailCounter?.alphaImprovement ??
        detailCounter?.AlphaImprovement ??
        nestedDetail?.alphaImprovement ??
        nestedDetail?.AlphaImprovement ??
        null
      );
    })(),
  };
};

/** Build save payload and clear inactive port slots (multi → single). */
export const buildCounterSavePayloads = ({
  modalFormData,
  quantityDeliveryTermId,
  capable = false,
  counterDyed = null,
  counterUndyed = null,
  counterAlpha = null,
  preserveQuantity = false,
  originalRow = null,
}) => {
  const isFob = modalFormData.incotermId === FOB_INCOTERM_ID;
  const routeLabel = modalFormData.incotermRouteLabel || '';
  const { showPort2, showPort3 } = getCounterActivePorts({
    isFob,
    incotermPortType: modalFormData.incotermPortType,
    multiportOption: modalFormData.incotermMultiPortType,
    incotermRouteId: modalFormData.incotermRouteId,
    incotermRouteLabel: routeLabel,
  });

  const timelineType = modalFormData.timelineType || null;
  const toNullable = value => value || null;
  const toQuantity = value => parseNumericQuantity(value);

  const originalDeliveryBasis =
    originalRow?.deliveryBasis ??
    originalRow?.parcelSize ??
    modalFormData.deliveryBasis;
  const originalQty1 =
    originalRow?.parcelSize ??
    originalRow?.deliveryBasis ??
    modalFormData.quantityLocation1;
  const originalQty2 =
    originalRow?.parcelSize2 ?? modalFormData.quantityLocation2;
  const originalQty3 =
    originalRow?.parcelSize3 ?? modalFormData.quantityLocation3;

  const quantitySource = preserveQuantity
    ? {
        deliveryBasis: originalDeliveryBasis,
        quantityLocation1: originalQty1,
        quantityLocation2: originalQty2,
        quantityLocation3: originalQty3,
      }
    : {
        deliveryBasis: modalFormData.deliveryBasis,
        quantityLocation1: modalFormData.quantityLocation1,
        quantityLocation2: modalFormData.quantityLocation2,
        quantityLocation3: modalFormData.quantityLocation3,
      };

  const incotermPayload = {
    quantityDeliveryTermId,
    incotermId: modalFormData.incotermId,
    incotermPortType: isFob ? null : toNullable(modalFormData.incotermPortType),
    incotermRouteId: isFob ? toNullable(modalFormData.incotermRouteId) : null,
    incotermLocationId: isFob
      ? null
      : toNullable(modalFormData.incotermLocationId),
    incotermLocation2Id:
      !isFob && showPort2
        ? toNullable(modalFormData.incotermLocation2Id)
        : null,
    incotermLocation3Id:
      !isFob && showPort3
        ? toNullable(modalFormData.incotermLocation3Id)
        : null,
    incotermFOB: isFob ? modalFormData.incotermFOB || null : null,
    incotermFOB2:
      isFob && showPort2 ? modalFormData.incotermLocation2Id || null : null,
    multiportOption:
      !isFob && modalFormData.incotermPortType === 'MultiPort'
        ? toNullable(modalFormData.incotermMultiPortType)
        : null,
    timelineType,
    startDate: toNullable(modalFormData.startDate),
    endDate: toNullable(modalFormData.endDate),
    timelineType2: showPort2
      ? (modalFormData.timelineType2 ?? timelineType)
      : null,
    startDate2: showPort2 ? toNullable(modalFormData.startDate2) : null,
    endDate2: showPort2 ? toNullable(modalFormData.endDate2) : null,
    timelineType3: showPort3
      ? (modalFormData.timelineType3 ?? timelineType)
      : null,
    startDate3: showPort3 ? toNullable(modalFormData.startDate3) : null,
    endDate3: showPort3 ? toNullable(modalFormData.endDate3) : null,
    loc1Draft1: modalFormData.loc1Draft1 ?? null,
    loc1Draft2: modalFormData.loc1Draft2 ?? null,
    loc1Loa1: modalFormData.loc1Loa1 ?? null,
    loc1Loa2: modalFormData.loc1Loa2 ?? null,
    loc1Dwt1: modalFormData.loc1Dwt1 ?? null,
    loc1Dwt2: modalFormData.loc1Dwt2 ?? null,
    loc1NotesForSTS: modalFormData.loc1NotesForSTS ?? null,
    loc2Draft1: showPort2 ? (modalFormData.loc2Draft1 ?? null) : null,
    loc2Draft2: showPort2 ? (modalFormData.loc2Draft2 ?? null) : null,
    loc2Loa1: showPort2 ? (modalFormData.loc2Loa1 ?? null) : null,
    loc2Loa2: showPort2 ? (modalFormData.loc2Loa2 ?? null) : null,
    loc2Dwt1: showPort2 ? (modalFormData.loc2Dwt1 ?? null) : null,
    loc2Dwt2: showPort2 ? (modalFormData.loc2Dwt2 ?? null) : null,
    loc2NotesForSTS: showPort2 ? (modalFormData.loc2NotesForSTS ?? null) : null,
    loc3Draft1: showPort3 ? (modalFormData.loc3Draft1 ?? null) : null,
    loc3Draft2: showPort3 ? (modalFormData.loc3Draft2 ?? null) : null,
    loc3Loa1: showPort3 ? (modalFormData.loc3Loa1 ?? null) : null,
    loc3Loa2: showPort3 ? (modalFormData.loc3Loa2 ?? null) : null,
    loc3Dwt1: showPort3 ? (modalFormData.loc3Dwt1 ?? null) : null,
    loc3Dwt2: showPort3 ? (modalFormData.loc3Dwt2 ?? null) : null,
    loc3NotesForSTS: showPort3 ? (modalFormData.loc3NotesForSTS ?? null) : null,
    additionalInformation: modalFormData.additionalInformation ?? null,
  };

  const quantityInformationPayload = {
    quantityDeliveryTermId,
    quantity: toQuantity(quantitySource.deliveryBasis),
  };

  const detailPayload = {
    isCapableToProviding: capable,
    quantityLocation1:
      showPort2 || showPort3
        ? toQuantity(quantitySource.quantityLocation1)
        : toQuantity(
            quantitySource.quantityLocation1 || quantitySource.deliveryBasis
          ),
    quantityLocation2: showPort2
      ? toQuantity(quantitySource.quantityLocation2)
      : null,
    quantityLocation3: showPort3
      ? toQuantity(quantitySource.quantityLocation3)
      : null,
    dyed: toQuantity(counterDyed),
    undyed: toQuantity(counterUndyed),
    alpha: toQuantity(counterAlpha),
  };

  return { incotermPayload, quantityInformationPayload, detailPayload };
};

/**
 * get-delivery-detail does not always join counter tables.
 * Enrich rows from quantity-*-counter endpoints so Counter UI can render.
 */
export const enrichDeliveryItemsWithCounterOffers = async ({
  quantityDeliveryTermId,
  token,
  items = [],
  incotermTypeOptions = [],
  incotermRouteOptions = [],
  requireIncotermCounter = false,
}) => {
  if (!items.length || !quantityDeliveryTermId || !token) return items;

  const [incotermResult, informationResult] = await Promise.all([
    fetchCounterJson(
      `quantity-incoterm-counter?QuantityDeliveryTermId=${quantityDeliveryTermId}`,
      token
    ),
    fetchCounterJson(
      `quantity-information-counter?QuantityDeliveryTermId=${quantityDeliveryTermId}`,
      token
    ),
  ]);

  const incotermCounters = unwrapCounterList(incotermResult);
  const informationCounters = unwrapCounterList(informationResult);

  if (!incotermCounters.length && !informationCounters.length) {
    return items.map(item => ({
      ...item,
      ...EMPTY_COUNTER_ROW_FIELDS,
      ...pickPreservedCounterPricing(item),
    }));
  }

  const incotermByFk = new Map();
  incotermCounters.forEach(item => {
    const fk = String(
      item?.quantityIncotermFK ??
        item?.QuantityIncotermFK ??
        item?.quantityIncotermId ??
        ''
    );
    if (fk) incotermByFk.set(fk, item);
  });

  const informationByFk = new Map();
  informationCounters.forEach(item => {
    const fk = String(
      item?.quantityInformationFK ??
        item?.QuantityInformationFK ??
        item?.quantityInformationId ??
        ''
    );
    if (fk) informationByFk.set(fk, item);
  });

  const informationById = new Map();
  informationCounters.forEach(item => {
    const id = String(item?.id ?? item?.Id ?? '');
    if (id) informationById.set(id, item);
  });

  const uniqueDetailFks = [
    ...new Set(
      items
        .map(item => String(item.quantityInformationDetailId ?? ''))
        .filter(Boolean)
    ),
  ];

  const detailListByFk = new Map();
  await Promise.all(
    uniqueDetailFks.map(async detailFk => {
      const relatedItems = items.filter(
        item => String(item.quantityInformationDetailId ?? '') === detailFk
      );
      const hasParent = relatedItems.some(sourceItem => {
        const hasIncotermParent = incotermByFk.has(
          String(sourceItem?.quantityIncotermId ?? '')
        );
        return (
          hasIncotermParent ||
          (!requireIncotermCounter &&
            informationByFk.has(
              String(sourceItem?.quantityInformationId ?? '')
            ))
        );
      });
      if (!hasParent) {
        detailListByFk.set(detailFk, []);
        return;
      }

      try {
        const result = await fetchCounterJson(
          `q-information-detail-counter?QuantityInformationDetailFK=${encodeURIComponent(detailFk)}`,
          token
        );
        detailListByFk.set(detailFk, unwrapCounterList(result) ?? []);
      } catch (error) {
        detailListByFk.set(detailFk, []);
      }
    })
  );

  const pickDetailCounter = ({
    detailFk,
    incotermCounter,
    informationCounter,
  }) => {
    const list = detailListByFk.get(String(detailFk ?? '')) ?? [];
    if (!list.length) return null;

    const incotermCounterId = String(
      incotermCounter?.id ?? incotermCounter?.Id ?? ''
    );
    if (incotermCounterId) {
      const byIncoterm = list.find(
        entry =>
          String(
            entry?.quantityIncotermCounterId ??
              entry?.QuantityIncotermCounterId ??
              ''
          ) === incotermCounterId
      );
      if (byIncoterm) return byIncoterm;
    }

    const informationCounterId = String(
      informationCounter?.id ?? informationCounter?.Id ?? ''
    );
    if (informationCounterId) {
      const byInformation = list.find(
        entry =>
          String(
            entry?.quantityInformationCounterId ??
              entry?.QuantityInformationCounterId ??
              ''
          ) === informationCounterId
      );
      if (byInformation) return byInformation;
    }

    return list.length === 1 ? list[0] : null;
  };

  const enriched = items.map(item => {
    const incotermCounter = incotermByFk.get(
      String(item.quantityIncotermId ?? '')
    );
    const informationCounterFromFk = informationByFk.get(
      String(item.quantityInformationId ?? '')
    );

    if (requireIncotermCounter && !incotermCounter) {
      return {
        ...item,
        ...EMPTY_COUNTER_ROW_FIELDS,
        ...pickPreservedCounterPricing(item),
      };
    }

    const detailCounter = pickDetailCounter({
      detailFk: item.quantityInformationDetailId,
      incotermCounter,
      informationCounter: informationCounterFromFk,
    });

    if (!detailCounter && !incotermCounter) {
      return {
        ...item,
        ...EMPTY_COUNTER_ROW_FIELDS,
        ...pickPreservedCounterPricing(item),
      };
    }

    const informationCounter = detailCounter
      ? (informationById.get(
          String(
            detailCounter?.quantityInformationCounterId ??
              detailCounter?.QuantityInformationCounterId ??
              ''
          )
        ) ??
        ((incotermCounter || !requireIncotermCounter) &&
        informationCounterFromFk
          ? informationCounterFromFk
          : null))
      : (incotermCounter || !requireIncotermCounter) && informationCounterFromFk
        ? informationCounterFromFk
        : null;

    const mappedCounter = mapCounterEntitiesToRowFields({
      incotermCounter,
      informationCounter,
      detailCounter,
      incotermTypeOptions,
      incotermRouteOptions,
    });
    const hasCounterAlpha = [mappedCounter.counterAlpha].some(
      value =>
        value !== null &&
        value !== undefined &&
        String(value).trim() !== '' &&
        Number(value) !== 0
    );
    const hasCounterDyed =
      mappedCounter.counterDyed !== null &&
      mappedCounter.counterDyed !== undefined &&
      String(mappedCounter.counterDyed).trim() !== '';
    const hasCounterUndyed =
      mappedCounter.counterUndyed !== null &&
      mappedCounter.counterUndyed !== undefined &&
      String(mappedCounter.counterUndyed).trim() !== '';

    const resolveCounterAlphaFallback = () => {
      const candidates = [
        item.counterAlpha,
        item.alpha2,
        item.Alpha2,
        item.alphaMitra2,
        item.AlphaMitra2,
      ];
      const found = candidates.find(
        value =>
          value !== null &&
          value !== undefined &&
          String(value).trim() !== '' &&
          Number(value) !== 0
      );
      return found ?? '';
    };

    return {
      ...item,
      ...EMPTY_COUNTER_ROW_FIELDS,
      ...mappedCounter,
      counterAlpha: hasCounterAlpha
        ? mappedCounter.counterAlpha
        : resolveCounterAlphaFallback(),
      counterDyed: hasCounterDyed
        ? mappedCounter.counterDyed
        : (item.counterDyed ?? item.dyed2 ?? item.Dyed2 ?? ''),
      counterUndyed: hasCounterUndyed
        ? mappedCounter.counterUndyed
        : (item.counterUndyed ?? item.undyed2 ?? item.Undyed2 ?? ''),
    };
  });

  const informationCounterIds = [
    ...new Set(
      enriched
        .map(item => item.quantityInformationCounterId)
        .filter(Boolean)
        .map(String)
    ),
  ];

  if (!informationCounterIds.length) return enriched;

  const resolveCounterCargoRatioId = value => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object') {
      return resolveCounterCargoRatioId(
        value.id ??
          value.Id ??
          value.cargoRatio ??
          value.CargoRatio ??
          value.ratioLpgId ??
          value.RatioLpgId ??
          null
      );
    }
    const ratioId = Number(value);
    if (!Number.isFinite(ratioId) || ratioId < 1 || ratioId > 5) return null;
    return ratioId;
  };

  const mapCargoRatioCounterEntries = list =>
    list
      .map(entry => {
        const ratioId = resolveCounterCargoRatioId(
          entry?.cargoRatio ??
            entry?.CargoRatio ??
            entry?.ratioLpgId ??
            entry?.RatioLpgId ??
            null
        );
        if (!ratioId) return null;
        const alpha =
          entry?.alpha ??
          entry?.Alpha ??
          entry?.alpha2 ??
          entry?.Alpha2 ??
          null;
        const alphaImprovement =
          entry?.alphaImprovement ?? entry?.AlphaImprovement ?? null;
        return {
          id: ratioId,
          label: CARGO_RATIO_LABELS[ratioId - 1] ?? String(ratioId),
          recordId: entry?.id ?? entry?.Id ?? null,
          quantityInformationCargoRatioFK:
            entry?.quantityInformationCargoRatioFK ??
            entry?.QuantityInformationCargoRatioFK ??
            entry?.quantityInformationCargoRatioId ??
            null,
          quantityIncotermCounterFK:
            entry?.quantityIncotermCounterFK ??
            entry?.QuantityIncotermCounterFK ??
            null,
          parcelSize: entry?.parcelSize ?? entry?.ParcelSize ?? null,
          alpha:
            alpha === null || alpha === undefined || alpha === '' ? '' : alpha,
          alphaImprovement,
          alphaHps: entry?.alphaHps ?? entry?.AlphaHps ?? null,
          alphaDyedHps: entry?.alphaDyedHps ?? entry?.AlphaDyedHps ?? null,
          alphaUndyedHps:
            entry?.alphaUndyedHps ?? entry?.AlphaUndyedHps ?? null,
          cargoRatioSnapshot: {
            id: entry?.id ?? entry?.Id ?? null,
            quantityInformationCargoRatioFK:
              entry?.quantityInformationCargoRatioFK ??
              entry?.QuantityInformationCargoRatioFK ??
              null,
            quantityInformationCounterFK:
              entry?.quantityInformationCounterFK ??
              entry?.QuantityInformationCounterFK ??
              null,
            quantityIncotermCounterFK:
              entry?.quantityIncotermCounterFK ??
              entry?.QuantityIncotermCounterFK ??
              null,
            cargoRatio: ratioId,
            parcelSize: entry?.parcelSize ?? entry?.ParcelSize ?? null,
            alpha:
              alpha === null || alpha === undefined || alpha === ''
                ? null
                : alpha,
            alphaImprovement,
            alphaHps: entry?.alphaHps ?? entry?.AlphaHps ?? null,
            alphaDyedHps: entry?.alphaDyedHps ?? entry?.AlphaDyedHps ?? null,
            alphaUndyedHps:
              entry?.alphaUndyedHps ?? entry?.AlphaUndyedHps ?? null,
          },
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.id - b.id)
      // Dedupe per incoterm counter, bukan per ratio saja. Satu parcel counter bisa dipakai
      // beberapa incoterm counter dengan ratio yang sama tetapi alpha berbeda.
      .filter(
        (item, index, arr) =>
          arr.findIndex(
            other =>
              String(other.quantityInformationCargoRatioFK ?? '') ===
                String(item.quantityInformationCargoRatioFK ?? '') &&
              String(other.quantityIncotermCounterFK ?? '') ===
                String(item.quantityIncotermCounterFK ?? '')
          ) === index
      );

  const cargoRatioByInformationCounterId = new Map();
  await Promise.all(
    informationCounterIds.map(async counterId => {
      try {
        const result = await fetchCounterJson(
          `quantity-information-cargo-ratio-counter?QuantityInformationCounterFK=${encodeURIComponent(counterId)}`,
          token
        );
        let list = unwrapCounterList(result);

        if (!list.length) {
          const altResult = await fetchCounterJson(
            `quantity-information-cargo-ratio-counter?quantityInformationCounterFK=${encodeURIComponent(counterId)}`,
            token
          );
          list = unwrapCounterList(altResult);
        }

        cargoRatioByInformationCounterId.set(
          counterId,
          mapCargoRatioCounterEntries(list)
        );
      } catch (error) {
        cargoRatioByInformationCounterId.set(counterId, []);
      }
    })
  );

  // Fallback: resolve via original cargo-ratio FKs when CounterFK query is empty
  const missingCounterIds = informationCounterIds.filter(
    id => !(cargoRatioByInformationCounterId.get(id) || []).length
  );

  if (missingCounterIds.length) {
    const cargoRatioFks = [
      ...new Set(
        enriched
          .filter(item =>
            missingCounterIds.includes(
              String(item.quantityInformationCounterId ?? '')
            )
          )
          .map(item => item.quantityInformationCargoRatioId)
          .filter(Boolean)
          .map(String)
      ),
    ];

    await Promise.all(
      cargoRatioFks.map(async cargoRatioFk => {
        try {
          const result = await fetchCounterJson(
            `quantity-information-cargo-ratio-counter?QuantityInformationCargoRatioFK=${encodeURIComponent(cargoRatioFk)}`,
            token
          );
          const list = unwrapCounterList(result);
          list.forEach(entry => {
            const counterFk = String(
              entry?.quantityInformationCounterFK ??
                entry?.QuantityInformationCounterFK ??
                ''
            );
            if (!missingCounterIds.includes(counterFk)) return;
            const mapped = mapCargoRatioCounterEntries([entry]);
            const prev = cargoRatioByInformationCounterId.get(counterFk) || [];
            cargoRatioByInformationCounterId.set(
              counterFk,
              mapCargoRatioCounterEntries([...prev, ...mapped])
            );
          });
        } catch {
          void 0;
        }
      })
    );
  }

  return enriched.map(item => {
    const counterId = item.quantityInformationCounterId;
    if (!counterId) return item;
    const allForCounter =
      cargoRatioByInformationCounterId.get(String(counterId)) ?? [];
    // Ambil hanya baris milik incoterm counter baris ini. Baris legacy tanpa FK incoterm
    // dipakai sebagai cadangan supaya data lama tetap tampil.
    const incotermCounterId = String(item.quantityIncotermCounterId ?? '');
    const scoped = incotermCounterId
      ? allForCounter.filter(
          ratio =>
            String(ratio.quantityIncotermCounterFK ?? '') === incotermCounterId
        )
      : [];
    const legacy = allForCounter.filter(
      ratio => !ratio.quantityIncotermCounterFK
    );
    const counterCargoRatios = scoped.length ? scoped : legacy;
    const counterAlpha = item.counterAlpha;
    const hasCounterAlpha =
      counterAlpha !== null &&
      counterAlpha !== undefined &&
      String(counterAlpha).trim() !== '' &&
      Number(counterAlpha) !== 0;
    const pickRatioAlpha = ratio => {
      const value = ratio?.alpha;
      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        Number(value) !== 0
      ) {
        return value;
      }
      return hasCounterAlpha ? counterAlpha : '';
    };
    const counterRatioAlphaByKey = Object.fromEntries(
      counterCargoRatios.map(ratio => [
        getCargoRatioKey(ratio.id),
        pickRatioAlpha(ratio),
      ])
    );
    const ratiosWithAlpha = counterCargoRatios.map(ratio => ({
      ...ratio,
      alpha: pickRatioAlpha(ratio),
      cargoRatioAlphaImprovement: ratio.alphaImprovement ?? null,
    }));
    return {
      ...item,
      counterCargoRatios: ratiosWithAlpha,
      counterRatioAlphaByKey,
    };
  });
};

export const isCounterValueChanged = (original, counter) =>
  counter !== null &&
  counter !== undefined &&
  counter !== '' &&
  String(original ?? '').trim() !== String(counter).trim();

export const isCounterQuantityChanged = (original, counter) => {
  if (counter === null || counter === undefined || counter === '') return false;
  const normalize = value => String(value ?? '').replace(/[^\d.-]/g, '');
  const originalQty = normalize(original);
  const counterQty = normalize(counter);
  if (!counterQty) return false;
  return originalQty !== counterQty;
};

export const isCounterDateChanged = (
  originalStart,
  originalEnd,
  counterStart,
  counterEnd,
  originalFallback = null,
  counterFallback = null
) => {
  const originalDate = formatPortDateRange(
    originalStart,
    originalEnd,
    originalFallback
  );
  const counterDate = formatPortDateRange(
    counterStart,
    counterEnd,
    counterFallback
  );
  return counterDate !== '-' && originalDate !== counterDate;
};

export const formatPortLocation = value => {
  if (value === null || value === undefined) return '-';
  const text = String(value).trim();
  return text || '-';
};

export const formatParcelSize = (value, unitLabel = 'MB') => {
  const unit = unitLabel || 'MB';
  const unitPattern = new RegExp(`\\b${unit}\\b`, 'i');

  if (value === null || value === undefined) return `- ${unit}`;

  // LPG: same formatter as HpsParcel formatParcelSizes → formatNumber + MT
  if (unit === 'MT') {
    const raw = String(value)
      .replace(/\b(MB|MT)\b/gi, '')
      .replace(/,/g, '')
      .trim();
    const formatted = formatNumber(raw);
    if (!formatted || formatted === '-') return `- ${unit}`;
    return `${formatted} ${unit}`;
  }

  const numericValue = Number(value);
  const text = Number.isFinite(numericValue)
    ? String(numericValue)
    : String(value).trim();
  if (!text || text === '-') return `- ${unit}`;
  if (unitPattern.test(text)) return text;
  if (/\b(MB|MT)\b/i.test(text)) return text;
  return `${text} ${unit}`;
};

export const formatPortDateRange = (startDate, endDate, fallbackDate) => {
  const start = convertToDisplayDateFormatddmmmyyyy(startDate);
  const end = convertToDisplayDateFormatddmmmyyyy(endDate);

  if (start && end) return `${start} - ${end}`;
  if (start) return start;
  if (end) return end;

  if (fallbackDate === null || fallbackDate === undefined) return '-';
  const fallback = String(fallbackDate).trim();
  return fallback || '-';
};

export const formatTolerance = value =>
  value === null || value === undefined || value === ''
    ? '-'
    : String(value).includes('%')
      ? value
      : `${value}%`;

export const parseNumericQuantity = value => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = parseFloat(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

export const getDeliveryBasisTotal = deliveryBasis =>
  parseNumericQuantity(deliveryBasis) ?? 0;

/** FOB Origin route — single location only (same as Procurement). */
export const isFobOriginRoute = formData =>
  formData.incotermId === FOB_INCOTERM_ID &&
  (formData.incotermRouteId === STRAITS_ROUTE_ID ||
    formData.incotermRouteLabel === 'Origin');

export const isFobStraitsRoute = formData =>
  formData.incotermId === FOB_INCOTERM_ID &&
  formData.incotermRouteLabel === 'Straits';

/** FOB never uses dual location; only non-FOB MultiPort 2Port/3Port. */
export const isFobDualLocationRoute = () => false;

export const isTwoPortQuantitySplit = formData =>
  formData.incotermId !== FOB_INCOTERM_ID &&
  formData.incotermPortType === 'MultiPort' &&
  formData.incotermMultiPortType === '2Port';

export const isThreePortQuantitySplit = formData =>
  formData.incotermId !== FOB_INCOTERM_ID &&
  formData.incotermPortType === 'MultiPort' &&
  formData.incotermMultiPortType === '3Port';

export const needsLocationQuantity = formData =>
  isTwoPortQuantitySplit(formData) || isThreePortQuantitySplit(formData);

export const applyCounterQuantityChange = (formData, field, value) => {
  const total = getDeliveryBasisTotal(formData.deliveryBasis);
  const val = parseNumericQuantity(value);
  const isEmpty = value === '' || value === null || value === undefined;

  let quantityLocation1 = formData.quantityLocation1 ?? '';
  let quantityLocation2 = formData.quantityLocation2 ?? '';
  let quantityLocation3 = formData.quantityLocation3 ?? '';

  const twoPort = isTwoPortQuantitySplit(formData);
  const threePort = isThreePortQuantitySplit(formData);

  if (field === 'quantityLocation1') {
    if (isEmpty) {
      return {
        quantityLocation1: '',
        quantityLocation2: twoPort || threePort ? '' : quantityLocation2,
        quantityLocation3: threePort ? '' : quantityLocation3,
      };
    }
    if (val > total) return null;

    const remainder = total - val;
    quantityLocation1 = String(val);

    if (twoPort) {
      quantityLocation2 = String(remainder);
      quantityLocation3 = '';
    } else if (threePort) {
      const loc2 = Math.floor(remainder / 2);
      quantityLocation2 = String(loc2);
      quantityLocation3 = String(remainder - loc2);
    }

    return { quantityLocation1, quantityLocation2, quantityLocation3 };
  }

  if (field === 'quantityLocation2') {
    if (isEmpty) {
      return {
        quantityLocation1,
        quantityLocation2: '',
        quantityLocation3,
      };
    }
    if (val > total) return null;

    const remainder = total - val;
    quantityLocation2 = String(val);

    if (twoPort) {
      quantityLocation1 = String(remainder);
      quantityLocation3 = '';
    } else if (threePort) {
      const loc1 = Math.floor(remainder / 2);
      quantityLocation1 = String(loc1);
      quantityLocation3 = String(remainder - loc1);
    }

    return { quantityLocation1, quantityLocation2, quantityLocation3 };
  }

  if (field === 'quantityLocation3') {
    if (twoPort) return null;
    if (isEmpty) {
      return { quantityLocation1, quantityLocation2, quantityLocation3: '' };
    }
    if (val > total) return null;

    const remainder = total - val;
    quantityLocation3 = String(val);
    quantityLocation1 = String(Math.floor(remainder / 2));
    quantityLocation2 = String(remainder - Math.floor(remainder / 2));

    return { quantityLocation1, quantityLocation2, quantityLocation3 };
  }

  return null;
};
